import { createFileRoute } from '@tanstack/react-router'
import { LayersIcon, LightbulbIcon, StoreIcon, TagIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCogsOverview } from '@/api/cogs'
import type { CogsRow } from '@/api/cogs'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'

// COGS overview (UI-SCREENS C-01, 08b #203): per Variant its COGS per unit, the "estimated" flag,
// the Batches used and when it was computed, read from variant_cogs — no computation here.
// Owner and Staff; the current Vendor Price and the Suggested Vendor Price (08d #205) are Owner
// only (G2) — the API omits them for Staff. One shared list row per Variant.
export const Route = createFileRoute('/w/$slug/cogs/')({
  component: CogsScreen,
  staticData: { breadcrumb: 'cogs.title' },
})

const formatComputedAt = (iso: string) =>
  new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))

function CogsScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const overview = useCogsOverview(slug)
  const rows = overview.data?.items ?? []

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('cogs.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cogs.subtitle')}</p>
      </div>

      {overview.isPending ? (
        <LoadingSkeleton />
      ) : overview.isError || !overview.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : rows.length === 0 ? (
        <RecordListEmpty>{t('cogs.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {rows.map((row) => (
            <CogsRowItem key={row.id} slug={slug} row={row} showVendorPrice={overview.data.show_vendor_price} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// One Variant per row; the whole row opens its breakdown (C-02). The figure is the COGS per unit
// ("—" while it can't be computed, with the reason below), the caption when it was computed. The
// Owner-only Vendor Price and Suggested Vendor Price sit in the meta line.
function CogsRowItem({ slug, row, showVendorPrice }: { slug: string; row: CogsRow; showVendorPrice: boolean }) {
  const { t } = useTranslation()
  const suggested = row.suggested_vendor_price

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/cogs/$variantId" params={{ slug, variantId: String(row.id) }}>
          {row.product.name} · {row.name}
        </RecordLink>
      }
      badges={
        <>
          {row.cogs?.estimated ? <Badge variant="secondary">{t('cogs.estimated')}</Badge> : null}
          {row.archived ? <Badge variant="outline">{t('cogs.archived')}</Badge> : null}
        </>
      }
      meta={[
        { icon: TagIcon, label: t('variants.form.sku'), value: row.sku },
        row.cogs
          ? {
              icon: LayersIcon,
              label: t('cogs.batchesUsed'),
              value: `${t('cogs.batchesUsed')}: ${row.cogs.batches_used}`,
            }
          : null,
        showVendorPrice
          ? {
              icon: StoreIcon,
              label: t('cogs.vendorPrice'),
              value: `${t('cogs.vendorPrice')}: ${row.vendor_price === null ? t('cogs.noVendorPrice') : `Rp ${formatMoney(row.vendor_price)}`}`,
            }
          : null,
        suggested !== undefined
          ? {
              icon: LightbulbIcon,
              label: t('cogs.suggestedPrice'),
              value: `${t('cogs.suggestedPrice')}: ${suggested === null ? '—' : `Rp ${formatMoney(suggested)}`}`,
            }
          : null,
      ]}
      description={
        !row.cogs
          ? t('cogs.notComputable')
          : suggested === null
            ? t('cogs.suggestedNoMargin')
            : undefined
      }
      figure={row.cogs ? `Rp ${formatMoney(row.cogs.total)}` : '—'}
      caption={row.cogs ? formatComputedAt(row.cogs.computed_at) : undefined}
    />
  )
}
